
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { Article, Collection } from '@api/api';
import { ArticleCard } from '@components/common/Card/ArticleCard';
import { CollectionCard } from '@components/common/Card/CollectionCard';
import { Seo } from '@components/common/Seo';
import { Search } from '@components/search/Search';
import { useSearchContext } from '@contexts/SearchContext';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { fetchSearch, useSearch } from '@queries/useSearch';
import { fetchSearchFilters, useSearchFilters } from '@queries/useSearchFilters';
import {
  buildArticleLink,
  buildCollectionPageLink,
  buildSearchCanonicalPageLink,
} from '@utils/link';
import { withGlobalSettings } from '@utils/pageFetchEnhancer';
import { getActiveTabInfo, getResultCount, getTabs } from '@utils/search';
import { GetServerSideProps } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo } from 'react';
import { dehydrate } from 'react-query';
import { PageProps } from '../types/page';

const DEFAULT_RESULTS = [];

const renderCollectionData = (collection: Collection) => (
  <CollectionCard
    key={collection.id}
    collection={collection}
    href={buildCollectionPageLink(collection.slug)}
    variant="large"
    thumbnail="left"
    size="large"
  />
);

const renderArticleData = (article: Article) => (
  <ArticleCard
    article={article}
    href={buildArticleLink(article.id, article.slug)}
    variant="large"
    thumbnail="left"
    size="large"
  />
);

export default function SearchPage(): ReactElement {
  const { t } = useTranslation('search');
  const { search, setSearch } = useSearchContext();
  const debouncedSearchTerm = useDebouncedValue(search, 500);
  const router = useRouter();
  const { data: searchFilters } = useSearchFilters();
  const {
    query: { query = '', tab = 'all' },
  } = router;
  const searchQueryString = String(query);
  const activeTabInfo = useMemo(
    () => getActiveTabInfo(searchFilters, String(tab), t),
    [searchFilters, t, tab],
  );
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useSearch(
    searchQueryString,
    activeTabInfo,
  );
  const results = data?.pages ?? DEFAULT_RESULTS;
  const index = tab === 'collections' ? 'collection' : 'article';
  const searchData = results.map(({ data: { searchResults } }) => searchResults[index].hits).flat();
  const resultCount = useMemo(() => getResultCount(results), [results]);
  const tabs = useMemo(
    () => getTabs(searchFilters, resultCount, searchQueryString, t, activeTabInfo),
    [searchFilters, searchQueryString, resultCount, t, activeTabInfo],
  );

  const isSearchQueryFilled = Boolean(searchQueryString.length);
  const noResultsMessage = isSearchQueryFilled
    ? t('no-results-message', { query: searchQueryString })
    : t('no-results-message-empty-query');
  const loadingMessage = t('loading-message', { query: searchQueryString });

  useEffect(() => {
    setSearch(searchQueryString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQueryString]);

  useEffect(() => {
    router.push(
      {
        pathname: 'search',
        query: { query: debouncedSearchTerm, tab },
      },
      '',
      { shallow: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, tab]);

  const activeTabName = useMemo(
    () => tabs?.find((item) => item.id === activeTabInfo.name)?.name,
    [tabs, activeTabInfo.name],
  );

  const url = buildSearchCanonicalPageLink();

  return (
    <>
      <Seo noindex nofollow canonical={url} />
      <Search<Article | Collection>
        data={searchData}
        loading={isFetchingNextPage}
        onLoadMoreClick={fetchNextPage}
        loadMoreDisabled={isFetchingNextPage || !hasNextPage}
        loadMoreText={isLoading || !hasNextPage ? '' : t('load-more')}
        renderData={tab === 'collections' ? renderCollectionData : renderArticleData}
        navigationItems={tabs}
        activeNavigationItem={activeTabName}
        variant="list"
        shallowNavigation
        inputTitle={t('title')}
        headerTitle={t('title')}
        noResultsMessage={noResultsMessage}
        loadingMessage={loadingMessage}
      />
    </>
  );
}

 const _getServerSideProps: GetServerSideProps = withGlobalSettings<PageProps>(
  async (nextContext, { queryClient, adScriptsPromise, globalSettingsPromise }) => {
    const {
      query: { query = '', tab = 'all' },
    } = nextContext;

    try {
      const [adScripts, globalSettings] = await Promise.all([
        adScriptsPromise,
        globalSettingsPromise,
        queryClient.prefetchInfiniteQuery(['search', query, tab], () =>
          fetchSearch(String(query), { name: String(tab) }),
        ),
        queryClient.prefetchQuery(['search-filters'], fetchSearchFilters),
      ]);

      return {
        props: {
          query,
          dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
          adScripts,
          globalSettings,
        },
      };
    } catch (e) {
      return {
        notFound: true,
      };
    }
  },
);


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/search',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  